import { type ApiClientOptions, apiClient } from '@/lib/api-client';
import { useCallback } from 'react';

/**
 * Hook that provides access to the authenticated API client
 * with convenient callback wrappers
 */
export function useApiClient() {
  /**
   * GET request
   */
  const get = useCallback(async <T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method' | 'body'>) => {
    return apiClient.get<T>(url, options);
  }, []);

  /**
   * POST request
   */
  const post = useCallback(
    async <T = unknown>(url: string, body?: unknown, options?: Omit<ApiClientOptions, 'method' | 'body'>) => {
      return apiClient.post<T>(url, body, options);
    },
    [],
  );

  /**
   * PUT request
   */
  const put = useCallback(
    async <T = unknown>(url: string, body?: unknown, options?: Omit<ApiClientOptions, 'method' | 'body'>) => {
      return apiClient.put<T>(url, body, options);
    },
    [],
  );

  /**
   * PATCH request
   */
  const patch = useCallback(
    async <T = unknown>(url: string, body?: unknown, options?: Omit<ApiClientOptions, 'method' | 'body'>) => {
      return apiClient.patch<T>(url, body, options);
    },
    [],
  );

  /**
   * DELETE request
   */
  const del = useCallback(async <T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method'>) => {
    return apiClient.delete<T>(url, options);
  }, []);

  return {
    get,
    post,
    put,
    patch,
    delete: del,
    client: apiClient,
  };
}
